import { JSX } from "react"
import { Info } from "lucide-react"
import { HpcJob, JobStatus } from "@/types/hpc-jobs"
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip"

/**
 * Info icon shown next to an array job's status on the jobs and workflow lists,
 * warning that the parent's aggregate view can be out of date.
 *
 * The backend only refreshes a parent array job once every child task has
 * changed state, so while it is still running its status, times, cost and log
 * links lag behind the real child state. Finished parents (SUCCEEDED/FAILED)
 * are settled, and non-array jobs are never affected, so the icon renders
 * nothing (returns null) in those cases.
 *
 * @param props - Component props.
 * @param props.job - The job whose status badge this annotates.
 * @returns The info icon with a staleness tooltip, or null when not applicable.
 */
export function ArrayJobStalenessInfo({ job }: { job: HpcJob }): JSX.Element | null {
  const isArrayParent: boolean = (job.arraySize ?? 0) > 0
  const isFinished: boolean =
    job.status === JobStatus.SUCCEEDED || job.status === JobStatus.FAILED

  // Only ongoing array parents can be stale; everything else is accurate.
  if (!isArrayParent || isFinished) return null

  return (
    <Tooltip>
      <TooltipTrigger
        className="inline-flex items-center text-muted-foreground cursor-help"
        aria-label="Array job status may be out of date"
      >
        <Info className="size-3.5 shrink-0" aria-hidden="true" />
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        This summary can be out of date while the job runs. Open the array job for
        up-to-date child task status.
      </TooltipContent>
    </Tooltip>
  )
}
